import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Badge, Button, Card, Input, Select, Space, Table, Tooltip, message } from "antd";
import {
  CalendarOutlined,
  CloudDownloadOutlined,
  CommentOutlined,
  FileImageOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import dayjs from "dayjs";

import { API_BASE_URL } from "../../utils/constants";
import { usePageTitle } from "../../hooks";
import type { UserInfo } from "../../types/auth";
import { PageHeader } from "../../components/common/PageHeader";
import { PlanningPlotMapsExportModal } from "../planning-groups/components/PlanningPlotMapsExportModal";
import "../planning-groups/planning-groups.css";

interface AuditorPlanningRow {
  id: number;
  name: string;
  start_date: string;
  finish_date: string;
  season_name?: string;
  country?: { name?: string } | string | null;
  users?: Array<{ id: number; name: string }>;
  total_audits?: number;
  pending_audits?: number;
  // Aggregated stats served by PlanningGroupListSerializer
  planned_surface?: number;
  audited_surface?: number;
  total_producers?: number;
  audited_producers?: number;
  total_farms?: number;
  audited_farms?: number;
  total_kg_audited?: number;
  // First / last realised audit date (replace the static start_date /
  // finish_date for display purposes — those still come back too).
  first_audit_date?: string | null;
  last_audit_date?: string | null;
  // Mensajes no leídos por el usuario en los hilos de comentarios de
  // esta planning — badge en la card / columna nombre.
  unread_comments_count?: number;
}

interface ApiResponse<T> {
  data: T[];
  total: number;
}

export const AuditorPlanningGroupsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isManager = identity?.app_user?.user_type === 4;

  const [rows, setRows] = useState<AuditorPlanningRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Filtros sincronizados con la URL — al volver del detalle de una
  // auditoría con el back del navegador, la URL conserva `?q=…` y
  // `?season=…` y ambos filtros se restauran solos sin re-aplicar.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchText = searchParams.get("q") ?? "";
  const seasonFilter = searchParams.get("season") ?? "";
  const setSearchText = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
  };
  const setSeasonFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value) prev.set("season", value);
        else prev.delete("season");
        return prev;
      },
      { replace: true },
    );
  };
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [mapsExportTarget, setMapsExportTarget] =
    useState<AuditorPlanningRow | null>(null);

  // Mismo bundle ZIP que la lista admin de /planning-groups: el
  // endpoint vive en la viewset estándar y se autentica con el JWT
  // del auditor, así que reutilizamos la misma URL.
  const handleDownloadBundle = async (record: AuditorPlanningRow) => {
    setDownloadingId(record.id);
    const hide = message.loading(
      t(
        "planning.downloading_bundle",
        "Generando paquete (KML + Excel + aforo)...",
      ),
      0,
    );
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(
        `${API_BASE_URL}/planning-groups/${record.id}/download-bundle/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `${record.name || "planning"}_bundle.zip`;
      const filename = match?.[1] || fallbackName;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      message.success(
        t("planning.download_success", "Paquete descargado correctamente"),
      );
    } catch (err) {
      message.error(
        t(
          "planning.download_error",
          "No se pudo generar el paquete. Inténtalo de nuevo.",
        ),
      );
      console.error("download-bundle failed", err);
    } finally {
      hide();
      setDownloadingId(null);
    }
  };

  const pageTitle = isManager
    ? t("auditor_section.plannings.manager_title")
    : t("auditor_section.plannings.title");
  usePageTitle(pageTitle);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("access_token") || "";
        const res = await fetch(
          `${API_BASE_URL}/v1/auditor/planning-groups/?page_size=100&ordering=-created`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: ctrl.signal,
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ApiResponse<AuditorPlanningRow>;
        setRows(json.data ?? []);
      } catch (e) {
        if ((e as any).name !== "AbortError") {
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Lista única de campañas presentes en las plannings cargadas —
  // alimenta el Select de filtro (antes era una columna). Se ordena
  // desc: las campañas nuevas suelen ser las que el auditor busca.
  const seasonOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const s = (r.season_name || "").trim();
      if (s) set.add(s);
    });
    return Array.from(set)
      .sort((a, b) => b.localeCompare(a))
      .map((s) => ({ label: s, value: s }));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const s = seasonFilter.trim();
    return rows.filter((r) => {
      if (s && (r.season_name || "") !== s) return false;
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.season_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, searchText, seasonFilter]);

  // Utilidad compartida: % completado y color del fill de la fila.
  // Mantiene misma paleta que la antigua columna Progress
  // (amber < 50 %, cyan 50-99 %, verde 100 %) — pero con alfa baja
  // para no tapar el texto de la fila.
  const getCompletionInfo = (record: AuditorPlanningRow) => {
    const total = record.total_audits ?? 0;
    const pending = record.pending_audits ?? 0;
    if (total === 0) return { pct: 0, color: "transparent" as string };
    const pct = Math.round(((total - pending) / total) * 100);
    const color =
      pct >= 100
        ? "rgba(82, 196, 26, 0.20)" // verde suave
        : pct >= 50
          ? "rgba(19, 194, 194, 0.18)" // cian suave
          : "rgba(250, 173, 20, 0.20)"; // ámbar suave
    return { pct, color };
  };

  // Column order matches the auditor's mental model: identity →
  // window → "what's being audited" (producers / fincas / audits) →
  // magnitudes (superficie, kilos) → progress (% completion at the
  // tail so the user can scan the whole row before judging
  // progress).
  const columns = [
    {
      title: t("planning.name"),
      dataIndex: "name",
      key: "name",
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        a.name.localeCompare(b.name),
      render: (name: string, record: AuditorPlanningRow) => {
        const unread = record.unread_comments_count ?? 0;
        if (unread <= 0) return name;
        return (
          <Space size={8}>
            <span>{name}</span>
            <Tooltip
              title={t(
                "planning.unread_comments_tooltip",
                "{{n}} comentario(s) sin leer en esta planning",
                { n: unread },
              )}
            >
              <Badge
                count={unread}
                overflowCount={99}
                style={{ backgroundColor: "#faad14" }}
                offset={[6, -2]}
              >
                <CommentOutlined
                  style={{ color: "#faad14", fontSize: 16 }}
                />
              </Badge>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      // Date of the *first realised audit* in the planning (audit
      // with `start_date` set), not the planning's formal start
      // window. Falls back to "—" until at least one audit happens.
      title: t("planning.start_date"),
      dataIndex: "first_audit_date",
      key: "first_audit_date",
      width: 130,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) => {
        const av = a.first_audit_date ? new Date(a.first_audit_date).getTime() : 0;
        const bv = b.first_audit_date ? new Date(b.first_audit_date).getTime() : 0;
        return av - bv;
      },
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format("DD/MM/YYYY") : "—",
    },
    {
      // Fecha fin dinámica: sólo se muestra cuando la planning está
      // efectivamente cerrada (>95 % completado). Antes se mostraba
      // en cuanto había alguna last_audit_date, lo que confundía —
      // un auditor abría el detalle asumiendo "ya está" y quedaban
      // pendientes.
      title: t("planning.finish_date"),
      dataIndex: "last_audit_date",
      key: "last_audit_date",
      width: 130,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) => {
        const ap = getCompletionInfo(a).pct;
        const bp = getCompletionInfo(b).pct;
        const av = ap > 95 && a.last_audit_date ? new Date(a.last_audit_date).getTime() : 0;
        const bv = bp > 95 && b.last_audit_date ? new Date(b.last_audit_date).getTime() : 0;
        return av - bv;
      },
      render: (v: string | null | undefined, record: AuditorPlanningRow) => {
        const { pct } = getCompletionInfo(record);
        if (pct <= 95 || !v) return "—";
        return dayjs(v).format("DD/MM/YYYY");
      },
    },
    {
      title: t("planning.producers", "Productores"),
      key: "producers_breakdown",
      width: 130,
      align: "center" as const,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        (a.audited_producers ?? 0) - (b.audited_producers ?? 0),
      render: (_: unknown, record: AuditorPlanningRow) => {
        const audited = record.audited_producers ?? 0;
        const total = record.total_producers ?? 0;
        return (
          <Tooltip
            title={t(
              "planning.producers_tooltip",
              "{{audited}} productores auditados de {{total}}",
              { audited, total },
            )}
          >
            <span>
              <strong>{audited}</strong>
              <span style={{ color: "#8c8c8c" }}> / {total}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("planning.farms", "Fincas"),
      key: "farms_breakdown",
      width: 130,
      align: "center" as const,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        (a.audited_farms ?? 0) - (b.audited_farms ?? 0),
      render: (_: unknown, record: AuditorPlanningRow) => {
        const audited = record.audited_farms ?? 0;
        const total = record.total_farms ?? 0;
        return (
          <Tooltip
            title={t(
              "planning.farms_tooltip",
              "{{audited}} fincas auditadas de {{total}}",
              { audited, total },
            )}
          >
            <span>
              <strong>{audited}</strong>
              <span style={{ color: "#8c8c8c" }}> / {total}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("planning.total_audits", "Total auditorías"),
      dataIndex: "total_audits",
      key: "total_audits",
      width: 130,
      align: "center" as const,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        (a.total_audits ?? 0) - (b.total_audits ?? 0),
      render: (_: unknown, record: AuditorPlanningRow) => {
        const total = record.total_audits ?? 0;
        const pending = record.pending_audits ?? 0;
        const realized = Math.max(total - pending, 0);
        return (
          <Tooltip
            title={t(
              "planning.audits_breakdown",
              "{{realized}} realizadas / {{pending}} pendientes",
              { realized, pending },
            )}
          >
            <span>
              <strong>{realized}</strong>
              <span style={{ color: "#8c8c8c" }}> / {total}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      // "Audited / Planned ha" in a single column so the auditor
      // sees how far through they are. Sorted by audited surface
      // (the actionable number).
      title: t("planning.surface", "Sup. auditada / planificada (ha)"),
      key: "surface_breakdown",
      width: 200,
      align: "right" as const,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        (a.audited_surface ?? 0) - (b.audited_surface ?? 0),
      render: (_: unknown, record: AuditorPlanningRow) => {
        const audited = record.audited_surface ?? 0;
        const planned = record.planned_surface ?? 0;
        const fmt = (n: number) =>
          new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 }).format(n);
        return (
          <Tooltip
            title={t(
              "planning.surface_tooltip",
              "{{audited}} ha auditadas de {{planned}} ha planificadas",
              { audited: fmt(audited), planned: fmt(planned) },
            )}
          >
            <span>
              <strong>{fmt(audited)}</strong>
              <span style={{ color: "#8c8c8c" }}> / {fmt(planned)}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("planning.total_kg_audited", "Kilos auditados"),
      dataIndex: "total_kg_audited",
      key: "total_kg_audited",
      width: 140,
      align: "right" as const,
      sorter: (a: AuditorPlanningRow, b: AuditorPlanningRow) =>
        (a.total_kg_audited ?? 0) - (b.total_kg_audited ?? 0),
      render: (v: number | undefined) =>
        new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(
          v ?? 0,
        ) + " kg",
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 130,
      fixed: "right" as const,
      align: "center" as const,
      render: (_: unknown, record: AuditorPlanningRow) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip
            title={t(
              "planning.download_bundle",
              "Descargar paquete (KML + Excel + aforo)",
            )}
          >
            <Button
              size="small"
              icon={<CloudDownloadOutlined />}
              loading={downloadingId === record.id}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadBundle(record);
              }}
              aria-label={t(
                "planning.download_bundle",
                "Descargar paquete (KML + Excel + aforo)",
              )}
            />
          </Tooltip>
          <Tooltip
            title={t(
              "planning.download_plot_maps",
              "Descargar mapas de parcelas de todas las auditorías",
            )}
          >
            <Button
              size="small"
              icon={<FileImageOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setMapsExportTarget(record);
              }}
              aria-label={t(
                "planning.download_plot_maps",
                "Descargar mapas de parcelas",
              )}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card>
        <PageHeader
          icon={<CalendarOutlined />}
          title={pageTitle}
          subtitle={
            isManager
              ? t("auditor_section.plannings.manager_subtitle")
              : t("auditor_section.plannings.subtitle")
          }
        />
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%" }}
        >
          <Space wrap size="middle">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t("auditor_section.plannings.search_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder={t("planning.season", "Campaña")}
              value={seasonFilter || undefined}
              onChange={(v) => setSeasonFilter(v ?? "")}
              options={seasonOptions}
              style={{ width: 200 }}
              showSearch
              optionFilterProp="label"
            />
          </Space>

          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: t("auditor_section.plannings.empty"),
            }}
            onRow={(record) => {
              const { pct, color } = getCompletionInfo(record);
              return {
                onClick: () => navigate(`/auditor/planning-groups/${record.id}`),
                className: "planning-progress-row",
                style: {
                  cursor: "pointer",
                  ["--progress-pct" as string]: `${pct}%`,
                  ["--progress-color" as string]: color,
                } as CSSProperties,
              };
            }}
          />
        </Space>
      </Card>
      {mapsExportTarget && (
        <PlanningPlotMapsExportModal
          open={!!mapsExportTarget}
          planningId={mapsExportTarget.id}
          planningName={
            mapsExportTarget.name || `planning-${mapsExportTarget.id}`
          }
          onClose={() => setMapsExportTarget(null)}
        />
      )}
    </>
  );
};

export default AuditorPlanningGroupsList;
